import { useState, useEffect, useMemo, useRef } from 'react'

import { Loading } from './../../../../Common/Loading'
import { LogErrors } from './../../../../../utilities/LogErrors'
import { useLanguage } from './../../../../../utilities/LanguageContext'

import { InstrumentData } from './../../../V2/Commons/InstrumentData'

/**
 * Mini componente con animación de plegado/desplegado usando max-height medido.
 * - Medimos scrollHeight para lograr una transición suave, incluso con imágenes.
 * - Incluye animación de opacidad.
 */
const Collapsible = ({ isOpen, children, duration = 250 }) => {
  const ref = useRef(null)
  const [maxHeight, setMaxHeight] = useState(isOpen ? 'auto' : 0)

  // Recalcular altura cuando se abre/cierra o cambia el contenido
  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Si se abre: fijar a scrollHeight y luego a 'auto' al terminar para adaptarse
    if (isOpen) {
      const h = el.scrollHeight
      setMaxHeight(h)
      const timer = setTimeout(() => setMaxHeight('auto'), duration)
      return () => clearTimeout(timer)
    } else {
      // Si estaba en auto, calculamos el alto real antes de colapsar
      if (maxHeight === 'auto') {
        const h = el.scrollHeight
        requestAnimationFrame(() => setMaxHeight(h))
        // siguiente frame, colapsar
        const timer = setTimeout(() => setMaxHeight(0), 0)
        return () => clearTimeout(timer)
      } else {
        setMaxHeight(0)
      }
    }
  }, [isOpen, children, duration]) // children por si cambia el contenido interno

  // Reaccionar a cambios de tamaño (imágenes que cargan, responsive, etc.)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      if (isOpen) {
        const h = el.scrollHeight
        if (maxHeight !== 'auto') setMaxHeight(h)
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [isOpen, maxHeight])

  return (
    <div
      style={{
        overflow: 'hidden',
        maxHeight: maxHeight === 'auto' ? 'none' : maxHeight,
        transition: `max-height ${duration}ms ease, opacity ${Math.max(150, duration - 50)}ms ease`,
        opacity: isOpen ? 1 : 0.02,
      }}
      aria-hidden={!isOpen}
    >
      <div ref={ref}>
        {children}
      </div>
    </div>
  )
}

/**
 * Botón/encabezado de sección.
 * - Para isMain:false muestra un botón toggle.
 * - Para isMain:true sólo muestra el título, sin toggle (siempre visible al inicio).
 */
const SectionHeader = ({ name, canToggle, isOpen, onToggle }) => {
  return (
    <div className="Concepts--V2__Concept__SectionHeader">
      {canToggle ? (
        <button
          type="button"
          className="Concepts--V2__Concept__SectionHeader__Button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={`concept-panel-${name}`}
        >
          {/* <span className="Concepts--V2__Concept__SectionHeader__Caret">{isOpen ? '▾' : '▸'}</span> */}
          <span className="Concepts--V2__Concept__SectionHeader__Caret">{isOpen ? <i className="bi bi-chevron-down"></i> : <i className="bi bi-chevron-right"></i>}</span>
          <span className="Concepts--V2__Concept__SectionHeader__Title">{name}</span>
        </button>
      ) : (
        <div className="Concepts--V2__Concept__SectionHeader__Static">
          <span className="Concepts--V2__Concept__SectionHeader__Dot"></span>
          <span className="Concepts--V2__Concept__SectionHeader__Title">{name}</span>
        </div>
      )}
    </div>
  )
}

/**
 * Concepts
 * @param {string}  topicName - Título del tema a mostrar
 * @param {object}  instrument - Objeto con "concept.parts"
 * @param {boolean} collapseNonMainByDefault - Si true, las secciones no principales inician plegadas
 */
const Concepts = ({ topicName, instrument, collapseNonMainByDefault = false, instrumentData = null }) => {
  const { translate } = useLanguage()

  const [isLoading, setIsLoading] = useState(true)
  const [parts, setParts] = useState([])

  // Estado de visibilidad por índice
  const [openMap, setOpenMap] = useState({}) // { [index]: boolean }

  // Inicializa parts y mapa de visibilidad
  const LoadPage = async () => {
    try {
      const list = instrument?.concept?.parts ?? []
      if (!Array.isArray(list) || list.length === 0) {
        setParts([])
        setOpenMap({})
        return
      }

      setParts(list)

      // Crear mapa de visibilidad:
      // - isMain:true => true siempre al inicio
      // - isMain:false => depende de collapseNonMainByDefault
      const initialMap = {}
      list.forEach((p, idx) => {
        initialMap[idx] = p.isMain ? true : !collapseNonMainByDefault
      })
      setOpenMap(initialMap)
    } catch (error) {
      await LogErrors('LoadPage - Concepts', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setIsLoading(true)
    LoadPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrument, topicName, collapseNonMainByDefault])

  const handleToggle = (idx) => {
    setOpenMap(prev => ({ ...prev, [idx]: !prev[idx] }))
  }

  // Sanitizador opcional:
  const safeHtml = (html) => {
    // return { __html: DOMPurify.sanitize(html || '') }
    return { __html: html || '' }
  }

  const hasContent = parts.length > 0

  return isLoading ? (
    <Loading />
  ) : (
    <div className='container Concepts--V2 Fonts--PlusJakartaSans'>
      {hasContent ? (
        <>
        {/* // <div className='Tables--Shadow'>
        //     <p className='Concepts--V2__Text--Title'>{topicName}</p>
        // </div> */}
        {/* <p className='Concepts--V2__Text--Title'>{topicName}</p> */}

        <InstrumentData data={instrumentData} />

        <div className='Concepts--V2__Concept'>

          <div className="Concepts__List" role="list">
            {parts.map((part, idx) => {
              const isMain = !!part.isMain
              const isOpen = !!openMap[idx]
              const canToggle = !isMain // Sólo las no principales son plegables

              return (
                <section key={`${part.name}-${idx}`} className="Concepts--V2__Concept__Section" role="listitem">
                  <SectionHeader
                    name={part.name}
                    canToggle={canToggle}
                    isOpen={isOpen}
                    onToggle={() => canToggle && handleToggle(idx)}
                  />

                  {/* Panel de contenido con animación */}
                  <Collapsible isOpen={isOpen}>
                    <div
                      id={`concept-panel-${part.name}`}
                      className="Concepts--V2__Concept__Section__Content"
                      // Si sanitizas, usa safeHtml(part.content)
                      dangerouslySetInnerHTML={safeHtml(part.content)}
                    />
                  </Collapsible>
                </section>
              )
            })}
          </div>
        </div>
        </>
      ) : (
        <p>{translate('labels.viewer.teaching.concepts.empty')}</p>
      )}
    </div>
  )
}

export { Concepts };